import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  SEQUENCE_WINDOW_MS,
  groupedShortcuts,
  isTypingTarget,
  matchSequence,
  matchSingleKey,
} from '@/lib/keyboardShortcuts';

/**
 * Global keyboard shortcuts, and the dialog that documents them.
 *
 * Both read the same list in lib/keyboardShortcuts, so a shortcut cannot be
 * advertised without working or work without being discoverable.
 */
export function KeyboardShortcuts() {
  const [, navigate] = useLocation();
  const [open, setOpen] = useState(false);
  /** First key of a pending `g`-prefixed sequence, if one is open. */
  const pending = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearPending = () => {
      pending.current = null;
      if (timer.current) clearTimeout(timer.current);
    };

    const run = (shortcut: ReturnType<typeof matchSingleKey>) => {
      if (!shortcut) return false;
      if (shortcut.action === 'showShortcuts') setOpen(true);
      else if (shortcut.action === 'openDialer') {
        window.dispatchEvent(new CustomEvent('openDialer', { detail: {} }));
      } else if (shortcut.href) navigate(shortcut.href);
      return true;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      // Never steal a keystroke from someone typing, and leave browser and OS
      // chords alone.
      if (isTypingTarget(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;

      if (pending.current) {
        const seq = matchSequence(pending.current, key);
        clearPending();
        if (seq) {
          e.preventDefault();
          run(seq);
        }
        return;
      }

      // `g` opens a sequence rather than doing anything on its own.
      if (key === 'g') {
        pending.current = 'g';
        timer.current = setTimeout(clearPending, SEQUENCE_WINDOW_MS);
        return;
      }

      // `?` arrives as a shifted key; match it before the single-key lookup.
      const single = matchSingleKey(key === '?' ? '?' : key);
      if (single) {
        e.preventDefault();
        run(single);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearPending();
    };
  }, [navigate]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="keyboard-shortcuts-dialog">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Jump between sections without reaching for the mouse.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {groupedShortcuts().map(([group, items]) => (
            <div key={group}>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-text-layer-muted">
                {group}
              </p>
              <ul className="space-y-1.5">
                {items.map((s) => (
                  <li key={s.label} className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-text-layer-secondary">{s.label}</span>
                    <span className="flex shrink-0 items-center gap-1">
                      {s.keys.map((k, i) => (
                        <span key={`${s.label}-${k}-${i}`} className="flex items-center gap-1">
                          {i > 0 && s.sequence && (
                            <span className="text-[11px] text-text-layer-muted">then</span>
                          )}
                          <kbd className="rounded border border-border bg-surface-elevated px-1.5 py-0.5 font-mono text-[11px] text-text-layer-primary">
                            {k}
                          </kbd>
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
